/**
 * 工作流日志系统
 * 提供结构化日志记录和查询功能
 */

import winston from 'winston';
import 'winston-daily-rotate-file';
import path from 'path';
import fs from 'fs-extra';
import { colors } from '../utils/colors';

export interface WorkflowLogEntry {
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  executionId?: string;
  workflowId?: string;
  nodeId?: string;
  event: string;
  message: string;
  data?: any;
  error?: {
    message: string;
    stack?: string;
    code?: string;
  };
}

export interface LoggerOptions {
  logDir?: string;
  maxFiles?: string;
  maxSize?: string;
  level?: string;
  enableConsole?: boolean;
  enableFile?: boolean;
}

export class WorkflowLogger {
  private logger: winston.Logger;
  private logDir: string;
  private options: LoggerOptions;
  
  constructor(options: LoggerOptions = {}) {
    this.options = {
      logDir: path.join(process.cwd(), '.workflow', 'logs'),
      maxFiles: '14d',
      maxSize: '20m',
      level: process.env.LOG_LEVEL || 'info',
      enableConsole: true,
      enableFile: true,
      ...options
    };
    
    this.logDir = this.options.logDir!;
    this.initialize();
    this.logger = this.createLogger();
  }
  
  private initialize() {
    fs.ensureDirSync(this.logDir);
    fs.ensureDirSync(path.join(this.logDir, 'daily'));
    fs.ensureDirSync(path.join(this.logDir, 'errors'));
  }
  
  private createLogger(): winston.Logger {
    const transports: winston.transport[] = [];
    
    // 控制台输出
    if (this.options.enableConsole) {
      transports.push(
        new winston.transports.Console({
          format: winston.format.combine(
            winston.format.colorize(),
            winston.format.timestamp(),
            winston.format.printf(({ timestamp, level, message, ...meta }) => {
              const time = new Date(timestamp).toLocaleTimeString();
              let output = `${time} ${level}: ${message}`;
              
              if (meta.executionId) {
                output = `[${meta.executionId.substring(0, 8)}] ${output}`;
              }
              
              return output;
            })
          )
        })
      );
    }
    
    // 文件输出
    if (this.options.enableFile) {
      // 每日轮转日志
      transports.push(
        new winston.transports.DailyRotateFile({
          filename: path.join(this.logDir, 'daily', 'workflow-%DATE%.log'),
          datePattern: 'YYYY-MM-DD',
          maxSize: this.options.maxSize,
          maxFiles: this.options.maxFiles,
          format: winston.format.combine(
            winston.format.timestamp(),
            winston.format.json()
          )
        })
      );
      
      // 错误日志
      transports.push(
        new winston.transports.DailyRotateFile({
          filename: path.join(this.logDir, 'errors', 'error-%DATE%.log'),
          datePattern: 'YYYY-MM-DD',
          level: 'error',
          maxSize: this.options.maxSize,
          maxFiles: this.options.maxFiles,
          format: winston.format.combine(
            winston.format.timestamp(),
            winston.format.json()
          )
        })
      );
    }
    
    return winston.createLogger({
      level: this.options.level,
      transports
    });
  }
  
  /**
   * 记录工作流开始
   */
  workflowStart(executionId: string, workflowId: string, inputs?: any): void {
    this.log({
      level: 'info',
      executionId,
      workflowId,
      event: 'workflow:start',
      message: `Starting workflow: ${workflowId}`,
      data: { inputs }
    });
  }
  
  /**
   * 记录工作流完成
   */
  workflowComplete(executionId: string, workflowId: string, result: any): void {
    this.log({
      level: 'info',
      executionId,
      workflowId,
      event: 'workflow:complete',
      message: `Workflow completed: ${workflowId}`,
      data: {
        status: result.status,
        duration: result.duration,
        outputs: result.outputs
      }
    });
  }
  
  /**
   * 记录工作流错误
   */
  workflowError(executionId: string, workflowId: string, error: Error): void {
    this.log({
      level: 'error',
      executionId,
      workflowId,
      event: 'workflow:error',
      message: `Workflow failed: ${workflowId}`,
      error: {
        message: error.message,
        stack: error.stack
      }
    });
  }
  
  /**
   * 记录节点执行
   */
  nodeExecute(executionId: string, nodeId: string, nodeInfo: any): void {
    this.log({
      level: 'info',
      executionId,
      nodeId,
      event: 'node:execute',
      message: `Executing node: ${nodeInfo.name}`,
      data: {
        nodeType: nodeInfo.type,
        nodeName: nodeInfo.name
      }
    });
  }
  
  /**
   * 记录节点完成
   */
  nodeComplete(executionId: string, nodeId: string, result?: any): void {
    this.log({
      level: 'info',
      executionId,
      nodeId,
      event: 'node:complete',
      message: `Node completed: ${nodeId}`,
      data: { result }
    });
  }
  
  /**
   * 记录节点错误
   */
  nodeError(executionId: string, nodeId: string, error: Error): void {
    this.log({
      level: 'error',
      executionId,
      nodeId,
      event: 'node:error',
      message: `Node failed: ${nodeId}`,
      error: {
        message: error.message,
        stack: error.stack
      }
    });
  }
  
  /**
   * 记录节点重试
   */
  nodeRetry(executionId: string, nodeId: string, attempt: number, maxAttempts: number): void {
    this.log({
      level: 'warn',
      executionId,
      nodeId,
      event: 'node:retry',
      message: `Retrying node: ${nodeId} (attempt ${attempt}/${maxAttempts})`,
      data: { attempt, maxAttempts }
    });
  }
  
  /**
   * 记录条件评估
   */
  conditionEvaluate(executionId: string, nodeId: string, condition: any, result: boolean): void {
    this.log({
      level: 'debug',
      executionId,
      nodeId,
      event: 'condition:evaluate',
      message: `Condition evaluated: ${result}`,
      data: { condition, result }
    });
  }
  
  /**
   * 记录任务创建
   */
  taskCreated(executionId: string, nodeId: string, taskId: string, task: any): void {
    this.log({
      level: 'info',
      executionId,
      nodeId,
      event: 'task:created',
      message: `Task created: ${task.content}`,
      data: { taskId, task }
    });
  }
  
  /**
   * 记录审批请求
   */
  approvalRequested(executionId: string, nodeId: string, approvers: string[]): void {
    this.log({
      level: 'info',
      executionId,
      nodeId,
      event: 'approval:requested',
      message: `Approval requested from: ${approvers.join(', ')}`,
      data: { approvers }
    });
  }
  
  /**
   * 记录审批结果
   */
  approvalReceived(executionId: string, nodeId: string, approver: string, approved: boolean): void {
    this.log({
      level: 'info',
      executionId,
      nodeId,
      event: 'approval:received',
      message: `Approval ${approved ? 'granted' : 'denied'} by: ${approver}`,
      data: { approver, approved }
    });
  }
  
  /**
   * 记录性能指标
   */
  performance(executionId: string, metrics: any): void {
    this.log({
      level: 'debug',
      executionId,
      event: 'performance:metrics',
      message: 'Performance metrics collected',
      data: metrics
    });
  }
  
  /**
   * 通用日志方法
   */
  log(entry: WorkflowLogEntry): void {
    const logData = {
      timestamp: entry.timestamp || new Date(),
      level: entry.level,
      executionId: entry.executionId,
      workflowId: entry.workflowId,
      nodeId: entry.nodeId,
      event: entry.event,
      message: entry.message,
      data: entry.data,
      error: entry.error
    };
    
    this.logger.log(entry.level, entry.message, logData);
  }
  
  /**
   * 查询日志
   */
  async queryLogs(options: {
    executionId?: string;
    workflowId?: string;
    nodeId?: string;
    event?: string;
    level?: string;
    startTime?: Date;
    endTime?: Date;
    limit?: number;
  }): Promise<WorkflowLogEntry[]> {
    const logs: WorkflowLogEntry[] = [];
    const logFiles = await this.getLogFiles(options.startTime, options.endTime);
    
    for (const file of logFiles) {
      const content = await fs.readFile(file, 'utf-8');
      const lines = content.split('\n').filter(line => line.trim());
      
      for (const line of lines) {
        try {
          const entry = JSON.parse(line);
          
          // 过滤条件
          if (options.executionId && entry.executionId !== options.executionId) continue;
          if (options.workflowId && entry.workflowId !== options.workflowId) continue;
          if (options.nodeId && entry.nodeId !== options.nodeId) continue;
          if (options.event && entry.event !== options.event) continue;
          if (options.level && entry.level !== options.level) continue;
          
          logs.push(entry);
          
          if (options.limit && logs.length >= options.limit) {
            return logs;
          }
        } catch (error) {
          // 忽略解析错误
        }
      }
    }
    
    return logs;
  }
  
  /**
   * 获取日志文件列表
   */
  private async getLogFiles(startTime?: Date, endTime?: Date): Promise<string[]> {
    const dailyDir = path.join(this.logDir, 'daily');
    const files = await fs.readdir(dailyDir);
    
    return files
      .filter(file => file.endsWith('.log'))
      .map(file => path.join(dailyDir, file))
      .sort();
  }
  
  /**
   * 生成执行摘要
   */
  async generateExecutionSummary(executionId: string): Promise<any> {
    const logs = await this.queryLogs({ executionId });
    
    const summary = {
      executionId,
      startTime: null as Date | null,
      endTime: null as Date | null,
      duration: 0,
      nodeCount: new Set<string>(),
      errorCount: 0,
      warningCount: 0,
      events: [] as any[]
    };
    
    for (const log of logs) {
      if (log.event === 'workflow:start') {
        summary.startTime = log.timestamp;
      }
      if (log.event === 'workflow:complete' || log.event === 'workflow:error') {
        summary.endTime = log.timestamp;
      }
      if (log.nodeId) {
        summary.nodeCount.add(log.nodeId);
      }
      if (log.level === 'error') {
        summary.errorCount++;
      }
      if (log.level === 'warn') {
        summary.warningCount++;
      }
      
      // 收集关键事件
      if (['workflow:start', 'workflow:complete', 'workflow:error', 'node:error'].includes(log.event)) {
        summary.events.push({
          timestamp: log.timestamp,
          event: log.event,
          message: log.message,
          data: log.data
        });
      }
    }
    
    if (summary.startTime && summary.endTime) {
      summary.duration = summary.endTime.getTime() - summary.startTime.getTime();
    }
    
    return {
      ...summary,
      nodeCount: summary.nodeCount.size
    };
  }
  
  /**
   * 清理旧日志
   */
  async cleanupOldLogs(daysToKeep: number = 30): Promise<void> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const dailyDir = path.join(this.logDir, 'daily');
    const errorDir = path.join(this.logDir, 'errors');
    
    for (const dir of [dailyDir, errorDir]) {
      const files = await fs.readdir(dir);
      
      for (const file of files) {
        const filePath = path.join(dir, file);
        const stats = await fs.stat(filePath);
        
        if (stats.mtime < cutoffDate) {
          await fs.remove(filePath);
          this.logger.info(`Cleaned up old log file: ${file}`);
        }
      }
    }
  }
}